import { useNavigate, useParams } from "react-router-dom";
import { useGetOneExpedition, useUpdateRequest } from "@/api/expedition.api";
import { Loader2 } from "lucide-react";
import { GetOneExpeditionResponseDto } from "@/types/api/api-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import Paths from "@/constants/paths";
import { Button } from "@/components/ui/button";
import {
  ExpeditionUpdateFormData,
  ExpeditionUpdateFormSchema,
} from "@/types/forms/home/cotationUpdateForm.form";
import CommentIcon from "@/assets/icons/comment.svg?react";
import { useState } from "react";
import { InfoFile } from "../../../components/shared/infoFile";

export const UpdateRequestPage = () => {
  const { id } = useParams();

  const { data: expedition, isSuccess: isExpeditionSuccess } = useGetOneExpedition(id || "");

  if (!isExpeditionSuccess)
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8">
        <Loader2 className="text-brand700 h-10 w-10 animate-spin" />
      </div>
    );

  return <UpdateRequestForm expedition={expedition} />;
};

type Props = {
  expedition: GetOneExpeditionResponseDto;
};

const UpdateRequestForm = ({ expedition }: Props) => {
  const navigate = useNavigate();
  const [newFilesId, setNewFilesId] = useState<string[]>([]);

  const { mutateAsync: updateRequest, isPending, isSuccess } = useUpdateRequest();

  const client =
    expedition?.deliveryInfo?.company ||
    (expedition?.deliveryInfo?.firstName && expedition?.deliveryInfo?.lastName
      ? expedition?.deliveryInfo?.firstName + " " + expedition?.deliveryInfo?.lastName
      : "-");

  const methods = useForm<ExpeditionUpdateFormData>({
    defaultValues: {
      request: undefined,
    },
    resolver: zodResolver(ExpeditionUpdateFormSchema),
    mode: "onChange",
  });

  const submitForm: SubmitHandler<ExpeditionUpdateFormData> = async (data) => {
    try {
      await updateRequest({
        id: expedition.id,
        payload: {
          ...data,
          newFilesId,
        },
      });
      navigate(-1);
    } catch (error) {
      console.error("Error updating request:", error);
      return;
    }
  };

  return (
    <div className="flex h-fit flex-1 flex-col gap-6 p-4 sm:p-8">
      <div className="flex flex-col gap-8">
        <div className="flex flex-row items-center gap-2">
          <a
            href={Paths.home.validatedShipments}
            className="text-medium align-middle text-sm text-gray-600"
          >
            Expéditions validées
          </a>
          <span className="align-middle text-[#CBD5E1]">{`>`}</span>
          <h4 className="text-semibold align-middle text-sm text-[#06F]">{client}</h4>
        </div>
        <div className="flex flex-col gap-3">
          <h1 className="text-3xl font-semibold">Modifier l'expédition</h1>
          <p className="text-normal text-gray-600">
            Décrivez la modification que vous souhaitez apporter à l'expédition. Votre demande sera
            envoyée à l'équipe Pelican.
          </p>
        </div>
      </div>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(submitForm)}
          className="flex flex-1 flex-col items-center gap-6"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <div className="flex w-full flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6 md:max-w-[1000px]">
            <div className="flex flex-row items-start justify-start gap-4 self-start">
              <CommentIcon />
              <h2 className="text-xm font-semibold text-black">Votre demande</h2>
            </div>
            <textarea
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-normal hover:bg-gray-50"
              rows={10}
              cols={50}
              placeholder="Date de livraison souhaitée, emplacement spécifique de livraison etc…"
              {...methods.register("request")}
            />
          </div>
          <InfoFile setNewFilesId={setNewFilesId} newFilesId={newFilesId} />
          <div className="flex w-full flex-row gap-3 md:max-w-[1000px] md:flex-row">
            <Button
              disabled={isPending || isSuccess}
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigate(Paths.home.validatedShipments)}
            >
              Annuler
            </Button>
            <Button
              disabled={isPending || isSuccess}
              onClick={() => methods.handleSubmit(submitForm)}
              type="submit"
              className="flex-1"
            >
              {isPending ? <Loader2 className="animate-spin" /> : "Envoyer la demande"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
